/** @jsxImportSource @emotion/react */
import * as R from 'ramda';
import * as React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { COLUMN_SPACING, ROW_SPACING } from '../styles';
import { DISABLED } from '../../styles/styles';
import ErrorView from '../ErrorView';
import { BigNeutral, BigSecondary } from '../elements/buttons';
import { isNilOrEmpty, assoc } from '../../language/module';
import { InputArgs } from './inputArgs';
import { InputType } from './inputType';
import LabelGroup from '../elements/LabelGroup';
import * as FoV from '../../language/funcOrValue';
import ToggleSwitch from '../elements/ToggleSwitch';
import { Input } from '../elements/Input';
import { Dropdown } from '../elements/select/Dropdown';
import { getResource } from '../../language/resources/getResource';
import Select from 'react-select';


const LabelRow = css`
  flex-direction: row;
  align-items: center;
  cursor: pointer;
  
  & > span {
    flex-grow: 1;
    margin-bottom: 0;
  }
`;

type Props = {
  formDef: InputArgs[];
  values: object;
  validate?: (currentValue: object) => string | undefined;
  error?: string | undefined;
  onSubmit: (newValue: object) => void;
  rootCss?: SerializedStyles;
  roles: string[];
  resources: Record<string, any>;
};

type State = {
  value: object;
  error: string | undefined;
};


/**
 * AutoForm: a component that takes a model and an array of field configurations for that model, and makes a form to
 * update that model. The model can happily contain properties this component doesn't know about -- they won't be
 * altered. When the user is done editing the model to their heart's content, you'll get a submit event with the final
 * model.
 *
 * PureComponent: true
 * Connected to Redux: false
 */
export default class AutoForm extends React.PureComponent<Props, State> {
  public state: State = {
    value: this.props.values,
    error: undefined,
  };


  public makeChangeHandler = (propName: string, changeHandler?: (e: Event) => void) => (event) => {
    if (event === undefined || event === null) {
      return;
    }

    let value;

    // Handle react-select multi-select
    if (Array.isArray(event) && event.length && event[0].value !== undefined) {
      const options = event.map((option) => option.value);
      value = options.sort(
        (a, b) => this.props.roles.indexOf(a) - this.props.roles.indexOf(b)
      );
    } else if (event.target) {
      value = event.target.value;
    } else {
      value = event;
    }

    if (changeHandler)
    {
      changeHandler(event);
    }

    this.setState(R.assocPath([ 'value', propName ], value));
  };


  /**
   * It's more performant not to make a new event handler on every render -- this method creates a new event handler
   * function on this instance for each form field that needs to be handled so that they can be reused between renders.
   */
  public makeAllChangeHandlers = (
    configs: InputArgs[],
  ) => configs.forEach(config => this[ `on${config.propName}Change` ] = this.makeChangeHandler(config.propName, config.changeHandler));


  public onSubmit = event => {
    if (!event) {
      return;
    }

    event.preventDefault();

    const error = (this.props.validate || (() => undefined))(this.state.value);

    this.setState(prev => assoc('error', error, prev));

    if (!isNilOrEmpty(error)) {
      return;
    }

    this.props.onSubmit!(this.state.value);
  };


  public onReset = () => this.setState(R.mergeDeepRight(R.__, { value: this.props.values, error: undefined }));


  public hasChanges = (initialValue, value) => !R.equals(initialValue, value);


  public typeToInput = (config: InputArgs, value): React.ReactNode => {

    switch (config.type) {
      case InputType.PLAIN_TEXT:
        return <Input title={FoV.unwrap(config.title || '', config)}
                      type="text"
                      value={value}
                      onChange={this[ `on${config.propName}Change` ]}
                      disabled={FoV.unwrap(!!config.disable, config)}
                      css={css`pointer-events: initial;`}
                      {...config.extras}/>;

      case InputType.NUMBER:
        return <Input title={FoV.unwrap(config.title || '', config)}
                      type="number"
                      value={value}
                      onChange={(e) => {
                        const extras = config.extras as { max?: number; min?: number };
                        const max = extras.max;
                        const min = extras.min;
                        let newVal = Number(e.target.value);

                        if (!isNaN(newVal)) {
                          if (max !== undefined && newVal > max) newVal = max;
                          if (min !== undefined && newVal < min) newVal = min;
                        }

                        // Call your existing handler, but pass the clamped value
                        this[`on${config.propName}Change`]({
                          ...e,
                          target: { ...e.target, value: newVal },
                        });
                      }}
                      disabled={FoV.unwrap(!!config.disable, config)}
                      css={css`pointer-events: initial;`}
                      {...config.extras}/>;

      case InputType.TOGGLE:
        return <ToggleSwitch resources={this.props.resources}
                             isOn={!!value}
                             onToggle={this[ `on${config.propName}Change` ]}
                             title={FoV.unwrap(config.title || '', config)}
                             disable={FoV.unwrap(!!config.disable, config)}
                             rootCss={ FoV.unwrap(!!config.disable, config) ? DISABLED : css`pointer-events: initial;` }
                             {...config.extras}/>;

      case InputType.SELECT:
        // @ts-ignore -- extras have to pass remaining props (items, noItemsMessage
        return <Dropdown resources={this.props.resources}
                         onChange={this[ `on${config.propName}Change` ]}
                         title={FoV.unwrap(config.title || '', config)}
                         disabled={FoV.unwrap(!!config.disable, config)}
                         selectedItem={value}
                         rootCss={css`pointer-events: initial;`}
                         {...config.extras}/>;

      case InputType.MULTI:
        return <Select
                isMulti={true}
                onChange={this[ `on${config.propName}Change` ]}
                value={value?.map(role => ({value: role, label: role}))}
                {...config.extras}/>;
    }
  };


  public makeInput = (
    config: InputArgs,
  ) => <LabelGroup key={config.propName}
                   label={FoV.unwrap<string, InputArgs>(config.label, config)}
                   subLabel={FoV.unwrap<string, InputArgs>(config.subLabel || '', config)}
                   info={FoV.unwrap<string, InputArgs>(config.info || '', config)}
                   rootCss={css`
                     ${config.type === InputType.TOGGLE ? LabelRow : ''}; 
                     ${FoV.unwrap(config.css || '', config)};
                     ${config.disable ? DISABLED : ''}
                   `}>
    {this.typeToInput(config, this.state.value[ config.propName ])}
  </LabelGroup>;


  public render = () => {
    const hasChanges = this.hasChanges(this.props.values, this.state.value);

    return (
      <form css={css`
              display: flex;
              flex-direction: column;
              ${COLUMN_SPACING};
              ${this.props.rootCss};
            `}>

        { // Make Form Fields
          this.props.formDef.map(this.makeInput)
        }

        {/* Error */}
        <ErrorView error={this.state.error}/>

        {/* Buttons */}
        <div css={css`
                 display: flex;
                 flex-direction: row;
                 ${ROW_SPACING};
                 margin-top: 10px;
               `}>
          <BigNeutral type='submit'
                      onClick={this.onSubmit}
                      disabled={!hasChanges}>{getResource(this.props.resources, 'AUTO_FORM_SAVE')}</BigNeutral>
          {
            hasChanges && <BigSecondary onClick={this.onReset}>{getResource(this.props.resources, 'AUTO_FORM_RESET')}</BigSecondary>
          }
        </div>
      </form>
    );
  };


  public componentDidUpdate(prevProps: Props): void {
    if (this.props.formDef !== prevProps.formDef) {
      this.makeAllChangeHandlers(this.props.formDef);
    }

    if (this.props.error !== prevProps.error) {
      this.setState(prev =>
        assoc('error', this.props.error ?? prev.error, prev)
      );
    }

    if (this.props.values !== prevProps.values) {
      this.setState(prev =>
        assoc(
          'value',
          R.mergeDeepRight(prev.value, this.props.values),
          prev
        )
      );
    }
  }


  public constructor(props) {
    super(props);

    this.makeAllChangeHandlers(this.props.formDef);
  }
}
